import type {
	FilterRuleModel,
	Metric,
	OptimizationIDModel,
	StatisticsInterpretabilityResponse,
} from '@/shared/generated'
import type { MetricType } from '../lib/helpers'
import type { MetricsDataUnit } from '../model/types'
import type { FormattedMetric } from './MetricRow'

import { any, prop } from 'lodash/fp'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useMetricsQuery } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'
import { useInterpretabilityStatisticQuery } from '@/entities/optimization-statistic'

import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Skeleton } from '@/shared/components/ui/skeleton'

import {
	getMetricCurrency,
	getMetricDiffByType,
	getTypesByMetricSchema,
	selectMetrics,
} from '../lib/helpers'
import { MetricRow } from './MetricRow'

export default function OptimizationMetricsWidget({
	queryParams,
	isLoading: isDataLoading,
}: {
	queryParams: {
		optimizations: OptimizationIDModel[]
		filters: FilterRuleModel[]
	} | null
	isLoading: boolean
}) {
	const { t } = useTranslation()

	const currency = useSelectedCurrency()
	const metricsQuery = useMetricsQuery()
	const interpretabilityStatistic = useInterpretabilityStatisticQuery(
		{ ...queryParams!, currency },
		{
			enabled: !!queryParams || !!metricsQuery.data || !isDataLoading,
			refetchOnMount: true,
			staleTime: 0,
			select: (data) =>
				selectMetrics(
					(data as StatisticsInterpretabilityResponse)?.metrics as MetricType
				).map((data: [string, MetricsDataUnit]) =>
					formatMetric(data, metricsQuery.data!, currency)
				),
		}
	)
	const isLoading =
		any(prop('isLoading'), [interpretabilityStatistic, metricsQuery]) ||
		isDataLoading
	const isError = any(prop('isError'), [
		interpretabilityStatistic,
		metricsQuery,
	])
	if (isLoading) {
		return <LoadingPlaceholder />
	}
	if (isError) {
		return (
			<Alert variant='destructive'>
				<AlertTriangleIcon />
				<AlertDescription>
					{interpretabilityStatistic.error?.message || t('fatal_error_title')}
				</AlertDescription>
			</Alert>
		)
	}

	return (
		<div className='grid gap-1'>
			{!!interpretabilityStatistic.data &&
				!isLoading &&
				interpretabilityStatistic.data.map((item: FormattedMetric) => (
					<MetricRow key={item.key} item={item} />
				))}
		</div>
	)
}

function LoadingPlaceholder() {
	return (
		<div className='space-y-2'>
			<div className='flex flex-col items-center space-y-2'>
				<div className='flex w-full items-center justify-between'>
					<Skeleton className='h-5 w-20' />
					<Skeleton className='h-5 w-10' />
				</div>
				<div className='flex w-full items-center justify-between'>
					<Skeleton className='h-5 w-24' />
					<Skeleton className='h-5 w-12' />
				</div>
				<div className='flex w-full items-center justify-between'>
					<Skeleton className='h-5 w-28' />
					<Skeleton className='h-5 w-12' />
				</div>
			</div>
			<Skeleton className='h-44 w-full' />
		</div>
	)
}

const formatMetric = (
	[key, values]: [string, MetricsDataUnit],
	metricsSchema: Metric[],
	currency?: string
): FormattedMetric => {
	const metricSchema = metricsSchema?.find(
		({ name }) => name.split('.')[0] === `${key}`
	)
	const { dataType, diffType } = getTypesByMetricSchema(metricSchema!)
	const data = {
		type: dataType,
		values,
	}
	const diff = {
		type: diffType,
		value: getMetricDiffByType(values, diffType),
	}

	return {
		key,
		data,
		diff,
		values,
		description: metricSchema?.description || '',
		currency: getMetricCurrency(metricSchema, currency),
	}
}
